import { useCallback } from 'react';

import { useListStringGroupsLazyQuery } from 'src/generated/graphql';

export type StringGroupNameCheck = (name: string) => Promise<boolean>;

/**
 * Checks a candidate string group name, loaded list first and a
 * `listStringGroups` prefix probe second. Advisory only: it closes neither the
 * gap between this check and the write nor a concurrent create of the same name.
 */
export function useStringGroupNameCheck(
  namespaceId: string,
  loadedStringGroupIds: readonly string[],
): StringGroupNameCheck {
  const [listStringGroups] = useListStringGroupsLazyQuery({
    fetchPolicy: 'network-only',
  });

  return useCallback(
    async (name) => {
      // stringGroupId is lowercased server-side (DdbKeyField.NORMALIZER), so
      // "Homepage" collides with an existing "homepage".
      const newStringGroupId = name.trim().toLowerCase();
      if (newStringGroupId === '' || namespaceId === '') return false;

      if (
        loadedStringGroupIds.some((id) => id.toLowerCase() === newStringGroupId)
      ) {
        return true;
      }

      const { data, error } = await listStringGroups({
        variables: {
          namespaceId,
          stringGroupIdPrefix: newStringGroupId,
          // The prefix is a begins_with on the lowercased sort key, read in
          // ascending order, so an exact match sorts ahead of every longer hit
          // that shares the prefix — one item is enough to find it.
          pageSize: 1,
          includeLatestSnapshot: false,
        },
      });
      // Apollo resolves a GraphQL failure instead of throwing it, and reporting
      // a name as available on a failed probe would wave a duplicate through.
      if (error) throw error;
      // Compared rather than counted: the prefix also matches longer names, so
      // a non-empty page does not mean this name is taken.
      return (data?.listStringGroups?.stringGroups ?? []).some(
        (group) => group.stringGroupId.toLowerCase() === newStringGroupId,
      );
    },
    [listStringGroups, namespaceId, loadedStringGroupIds],
  );
}
